// === SAA:ICONS:CUSTOM-PRODUCT-ICONS ===
// Hand-drawn product icons for the four products phosphor v1 has no glyph for
// (motorcycle, scooter, camper van, caravan). Construction matches phosphor's
// duotone convention exactly — 256 viewBox, a reduced-opacity fill layer plus
// 16px round-capped stroke paths, color/size via phosphor's IconProps — so the
// product-icon registry treats these identically to library icons; no special
// cases, no hardcoded colors. Shapes are deliberately chunky: they must read
// at the 28px card-tile size.

import { forwardRef } from 'react';
import type { IconProps } from 'phosphor-react';

const createDuotoneIcon = (
  displayName: string,
  fillPath: string,
  strokePaths: string[],
  strokeCircles: Array<[number, number, number]>,
) => {
  const Icon = forwardRef<SVGSVGElement, IconProps>(
    ({ size = 24, color = 'currentColor', weight = 'duotone', mirrored: _mirrored, style, ...rest }, ref) => (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 256 256"
        fill="none"
        style={style}
        {...rest}
      >
        {weight === 'duotone' && <path d={fillPath} fill={color} opacity={0.2} />}
        {strokePaths.map((d, i) => (
          <path key={i} d={d} stroke={color} strokeWidth={16} strokeLinecap="round" strokeLinejoin="round" fill="none" />
        ))}
        {strokeCircles.map(([cx, cy, r], i) => (
          <circle key={`c${i}`} cx={cx} cy={cy} r={r} stroke={color} strokeWidth={16} fill="none" />
        ))}
      </svg>
    ),
  );
  Icon.displayName = displayName;
  return Icon;
};

// Sport-bike silhouette. The signal that survives 28px is the OPEN triangle
// between the wheels with an engine mass inside it (the scooter's is closed by
// its floorboard/step-through panel): tank wedge sloping toward the front,
// steeply raked fork, tail kicking up at the rear.
export const MotorcycleIcon = createDuotoneIcon(
  'MotorcycleIcon',
  'M104 124l44-12 24 4-24 20zM112 136h36v26h-36z',
  [
    'M60 104l44 20 44-12 24 4',
    'M168 116l28 68',
    'M60 184l44-60',
    'M112 136h36v26h-36z',
  ],
  [
    [60, 184, 30],
    [196, 184, 30],
  ],
);

export const ScooterIcon = createDuotoneIcon(
  'ScooterIcon',
  'M168 172l20-76h16l-16 76zM52 124h56v12H52z',
  [
    'M172 92h44',
    'M188 96l-20 76h-84',
    'M76 172v-36',
    'M52 136h56',
  ],
  [
    [60, 196, 26],
    [196, 196, 26],
  ],
);

export const CamperVanIcon = createDuotoneIcon(
  'CamperVanIcon',
  'M32 92a8 8 0 0 1 8-8h128l56 52v36H32z',
  [
    'M32 172V92a8 8 0 0 1 8-8h128l56 52v36h-24',
    'M168 84v88',
    'M32 172h24',
    'M100 172h56',
    'M56 108h40v24H56z',
  ],
  [
    [78, 180, 20],
    [180, 180, 20],
  ],
);

export const CaravanIcon = createDuotoneIcon(
  'CaravanIcon',
  'M40 168V116a32 32 0 0 1 32-32h96a32 32 0 0 1 32 32v52z',
  [
    'M200 168V116a32 32 0 0 0-32-32H72a32 32 0 0 0-32 32v52h42',
    'M126 168h74',
    'M200 164h32',
    'M64 112h44v28H64z',
  ],
  [
    [104, 188, 20],
  ],
);
